// Thin REST client. The admin access token lives only in memory (module
// state), never in localStorage, so it can't be read by a stray XSS'd
// third-party script that inspects storage — only the long-lived refresh
// token is persisted, which is the standard tradeoff for this pattern.
// No API keys ever pass through this file: the browser only ever talks to
// our own backend, which alone holds the Azure credentials server-side.

// Where the backend lives. Empty string = same origin as this page (the normal
// case: local dev through the Vite proxy, or a production build served from the
// same origin as the API). Set VITE_API_BASE_URL at BUILD time — it is baked
// into the bundle, like every Vite env var — when the frontend is deployed
// separately from the backend, e.g. frontend on Azure Static Web Apps with the
// backend on its own (on-prem or cloud) host: set it to that backend's public
// HTTPS URL, with no trailing slash, e.g. https://afa-api.example.org.
export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

const REFRESH_STORAGE_KEY = "afa_refresh_token";

let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken() {
  return accessToken;
}
export function setRefreshToken(token: string | null) {
  if (token) localStorage.setItem(REFRESH_STORAGE_KEY, token);
  else localStorage.removeItem(REFRESH_STORAGE_KEY);
}
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_STORAGE_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function parseBody(res: Response) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchanges the stored refresh token for a new access token. Every caller
 * (page load, a 401 retry, the operator socket) shares ONE in-flight request:
 * the server rotates refresh tokens, so two parallel refreshes with the same
 * token would make the second one fail and log the operator out.
 */
export async function refreshSession(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) {
          // Only sign out if the token we just tried is still the current
          // one — another tab may already have rotated it successfully, and
          // wiping storage then would throw away its fresh token.
          if (res.status === 401 && getRefreshToken() === refreshToken) {
            setRefreshToken(null);
            setAccessToken(null);
          }
          return false;
        }
        const body = await parseBody(res);
        setAccessToken(body.accessToken);
        setRefreshToken(body.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

/** fetch() with the admin bearer token attached; on a 401 it refreshes the session once and retries. */
async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const headers = new Headers(init.headers);
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    return fetch(input, { ...init, headers });
  };
  let res = await send();
  if (res.status === 401 && (await refreshSession())) res = await send();
  return res;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  query?: Record<string, string | undefined>;
}

export async function apiRequest<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = true, query } = opts;

  let url = `${API_BASE}/api${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v !== undefined) qs.set(k, v);
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  const init: RequestInit = {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };
  const res = auth ? await authedFetch(url, init) : await fetch(url, init);

  if (!res.ok) {
    const errBody = await parseBody(res);
    const message =
      (errBody && typeof errBody === "object" && "error" in errBody && String((errBody as any).error)) ||
      res.statusText ||
      "Request failed";
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await parseBody(res)) as T;
}

/** Downloads an authenticated endpoint as a file (a plain <a href> can't send the bearer token). */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const res = await authedFetch(`${API_BASE}/api${path}`);
  if (!res.ok) {
    const errBody = await parseBody(res);
    const message =
      (errBody && typeof errBody === "object" && "error" in errBody && String((errBody as any).error)) ||
      "Download failed";
    throw new ApiError(res.status, message);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function apiUpload<T = unknown>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  // FormData bodies can be re-sent, so the 401-refresh retry in authedFetch is safe here.
  const res = await authedFetch(`${API_BASE}/api${path}`, { method: "POST", body: form });
  if (!res.ok) {
    const errBody = await parseBody(res);
    const message =
      (errBody && typeof errBody === "object" && "error" in errBody && String((errBody as any).error)) ||
      "Upload failed";
    throw new ApiError(res.status, message);
  }
  return (await parseBody(res)) as T;
}
